import { useState } from 'react';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Monitor, RefreshCw, Send, Smartphone, Wifi, X} from 'lucide-react';

const SystemGuideModal = ({ isOpen, onClose }) => {
  const [expandedSection, setExpandedSection] = useState('connection');

  if (!isOpen) return null;

  const troubleshootingSteps = [
    {
      id: 'connection',
      icon: Wifi,
      title: 'Masalah Sambungan Internet',
      color: '#60A5FA',
      steps: [
        'Pastikan WiFi atau data anda aktif',
        'Cuba buka website lain untuk sahkan internet berfungsi',
        'Jika menggunakan VPN, cuba matikan sementara',
        'Restart router jika perlu'
      ]
    },
    {
      id: 'loading',
      icon: RefreshCw,
      title: 'Halaman Tidak Load / Stuck',
      color: '#4ADE80',
      steps: [
        'Tekan Ctrl+Shift+R (Windows) atau Cmd+Shift+R (Mac) untuk hard refresh',
        'Clear browser cache: Settings > Privacy > Clear Browsing Data',
        'Cuba buka dalam Incognito/Private mode',
        'Jika masih gagal, cuba browser lain (Chrome, Firefox, Safari)'
      ]
    },
    {
      id: 'mobile',
      icon: Smartphone,
      title: 'Masalah di Mobile',
      color: '#F59E0B',
      steps: [
        'Pastikan app/browser dikemaskini ke versi terbaru',
        'Cuba force close app dan buka semula',
        'Clear app cache: Settings > Apps > MSE SUPREME AI > Clear Cache',
        'Jika guna PWA, uninstall dan install semula'
      ]
    },
    {
      id: 'desktop',
      icon: Monitor,
      title: 'Masalah di Desktop',
      color: '#A78BFA',
      steps: [
        'Disable browser extensions sementara',
        'Check JavaScript enabled: Settings > Privacy > Site Settings > JavaScript',
        'Cuba resize window atau toggle fullscreen (F11)',
        'Update browser ke versi terbaru'
      ]
    },
    {
      id: 'error',
      icon: AlertTriangle,
      title: 'Error Message / API Error',
      color: '#EF4444',
      steps: [
        'Screenshot error message untuk rujukan',
        'Tunggu 1-2 minit dan cuba semula',
        'Jika "Network Error" - check internet connection',
        'Jika "403 Forbidden" - clear cookies dan login semula',
        'Jika masalah berterusan, hubungi admin dengan screenshot'
      ]
    }
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '500px',
        maxHeight: '85vh',
        background: 'var(--bg-card, #1a1a1a)',
        borderRadius: '20px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }} data-testid="system-guide-modal">
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: '700' }}>
              🔧 System Troubleshooting
            </h2>
            <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
              Ikut langkah di bawah untuk selesaikan masalah
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff'
            }}
            data-testid="close-system-guide"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '16px'
        }}>
          {/* Quick Check Banner */}
          <div style={{
            padding: '14px 16px',
            background: 'rgba(74, 222, 128, 0.1)',
            border: '1px solid rgba(74, 222, 128, 0.3)',
            borderRadius: '12px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <CheckCircle size={24} color="#4ADE80" />
            <div>
              <div style={{ fontWeight: '600', color: '#4ADE80', fontSize: '13px' }}>
                Quick Fix: Hard Refresh
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted, #888)' }}>
                Tekan <kbd style={{ 
                  background: '#333', 
                  padding: '2px 6px', 
                  borderRadius: '4px',
                  fontSize: '11px'
                }}>Ctrl+Shift+R</kbd> untuk refresh penuh
              </div>
            </div>
          </div>

          {/* Troubleshooting Sections */}
          {troubleshootingSteps.map((section) => (
            <div 
              key={section.id}
              style={{
                background: 'var(--bg-secondary, #111)',
                borderRadius: '12px',
                marginBottom: '10px',
                overflow: 'hidden',
                border: '1px solid var(--border-subtle, #333)'
              }}
            >
              <button
                onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 16px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-primary, #fff)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: `${section.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <section.icon size={18} color={section.color} />
                  </div>
                  <span style={{ fontWeight: '600', fontSize: '13px' }}>
                    {section.title}
                  </span>
                </div>
                {expandedSection === section.id ? (
                  <ChevronUp size={18} color="#888" />
                ) : (
                  <ChevronDown size={18} color="#888" />
                )}
              </button>

              {expandedSection === section.id && (
                <div style={{ 
                  padding: '0 16px 16px',
                  borderTop: '1px solid var(--border-subtle, #333)'
                }}>
                  <ol style={{ 
                    margin: '12px 0 0', 
                    paddingLeft: '20px',
                    color: 'var(--text-muted, #888)',
                    fontSize: '13px',
                    lineHeight: '1.8'
                  }}>
                    {section.steps.map((step, idx) => (
                      <li key={idx} style={{ marginBottom: '6px' }}>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid var(--border-subtle, #333)',
          background: 'var(--bg-secondary, #111)'
        }}>
          <div style={{ 
            fontSize: '12px', 
            color: 'var(--text-muted, #666)',
            textAlign: 'center',
            marginBottom: '12px'
          }}>
            Masih ada masalah selepas cuba semua?
          </div>
          <button
            onClick={() => {
              window.open('https://t.me/RealFtcAdmin', '_blank');
              onClose();
            }}
            style={{
              width: '100%',
              padding: '14px',
              background: 'linear-gradient(135deg, #E01E3A 0%, #C41830 100%)',
              border: 'none',
              borderRadius: '12px',
              color: '#fff',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            data-testid="contact-admin-btn"
          >
            <Send size={18} />
            Hubungi Admin via Telegram
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemGuideModal;
